import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { Form, InputField } from '@epoch-ui/components/Form';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalDescription,
  ModalFooter
} from '@epoch-ui/components/Modal/modal';
import { Button } from '@epoch-ui/components/Button';
import { CapacityType } from 'types/event.types';

import { CapacityModalSchema, type CapacityModalFormValues } from '../schemas';

export interface CapacityModalProps extends React.ComponentPropsWithoutRef<typeof Modal> {
  initialValues: Partial<CapacityModalFormValues>;
  onSubmit: (values: CapacityModalFormValues) => void | Promise<void> | Promise<unknown>;
}

export const CapacityModal: React.FC<CapacityModalProps> = ({
  initialValues,
  onSubmit,
  onOpenChange,
  ...props
}) => {
  const form = useForm<CapacityModalFormValues>({
    resolver: zodResolver(CapacityModalSchema),
    defaultValues: initialValues,
    mode: 'onChange'
  });
  const {
    formState: { isValid, isSubmitting, isDirty }
  } = form;

  const handleOpenChange = React.useCallback(
    (open: boolean): void => {
      // Reset form when modal is closed/cancelled
      if (!open) {
        form.reset(initialValues);
      }
      onOpenChange?.(open);
    },
    [initialValues, onOpenChange, form]
  );

  const handleSubmit = React.useCallback(
    async (data: CapacityModalFormValues): Promise<void> => {
      const capacityType =
        data.maxAttendees && data.maxAttendees > 0 ? CapacityType.LIMITED : CapacityType.UNLIMITED;
      await onSubmit({
        maxAttendees: data.maxAttendees,
        capacityType
      });
    },
    [onSubmit]
  );

  const handleRemoveLimit = (): void => {
    form.reset({ maxAttendees: null, capacityType: CapacityType.UNLIMITED });
    handleSubmit({ maxAttendees: null, capacityType: CapacityType.UNLIMITED });
  };

  return (
    <Modal {...props} onOpenChange={handleOpenChange}>
      <Form {...form}>
        <ModalContent>
          <form
            onSubmit={(e) => {
              // Prevent propagation "submit" event to outer <Form /> in case of nesting forms
              e.stopPropagation();
              form.handleSubmit(handleSubmit)(e);
            }}
          >
            <ModalHeader className="tw-mb-0 tw-flex tw-justify-between">
              <ModalTitle className="tw-text-[24px] tw-font-medium">Capacity</ModalTitle>
            </ModalHeader>
            <ModalDescription className="tw-mt-2 tw-text-sm tw-text-muted-foreground">
              Automatically close registration and create a waitlist when capacity is reached. Only
              registered guests count towards capacity.
            </ModalDescription>

            <InputField
              name="maxAttendees"
              label="Capacity"
              type="number"
              className="tw-my-5"
              inputClassName="tw-h-11 tw-w-full"
              min="1"
            />

            <ModalFooter className="tw-mt-0">
              {initialValues.maxAttendees ? (
                <div className="tw-grid tw-w-full tw-grid-cols-2 tw-gap-x-3">
                  <Button
                    type="button"
                    size="sm"
                    variant="destructive"
                    className="tw-h-10 tw-w-full tw-rounded-lg"
                    onClick={handleRemoveLimit}
                    disabled={isSubmitting}
                  >
                    Remove limit
                  </Button>
                  <Button
                    type="submit"
                    size="sm"
                    className="tw-h-10 tw-w-full tw-rounded-lg"
                    disabled={!isValid || isSubmitting || !isDirty}
                  >
                    Update limit
                  </Button>
                </div>
              ) : (
                <Button
                  type="submit"
                  size="sm"
                  className="tw-h-10 tw-w-full tw-rounded-lg"
                  disabled={!isValid || isSubmitting || !isDirty}
                >
                  Set limit
                </Button>
              )}
            </ModalFooter>
          </form>
        </ModalContent>
      </Form>
    </Modal>
  );
};
